import { makeStyles } from "@material-ui/core/styles";
import { useParams } from "react-router-dom";
import { Avatar, Box, Drawer, Typography, Fab, Tooltip, useTheme, useMediaQuery, Paper } from "@material-ui/core";
import { i18n } from "../../translate/i18n";
import classNames from "classnames";
import TicketManagementFilters from "../../components/TicketManagement/filters";
import TicketManagementList from "../../components/TicketManagement/list";
import Ticket from "../../components/Ticket";
import { useEffect, useState, useReducer, useContext, useRef } from "react";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import FilterListOutlinedIcon from '@material-ui/icons/FilterListOutlined';
import { AuthContext } from "../../context/Auth/AuthContext";
import socket from "../../hooks/useSocket";
import Logo from "../../assets/logo_login.png";

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    maxWidth: "100vw",
    margin: '20px 10px!important',
  },
  title: {
    fontSize: "1.3rem",
    fontWeight: "800",
    color: "#616161"
  },
  content: {
    display: "flex",
    height: "calc(100vh - 105px)"
  },
  baseContent: {
    padding: "1rem",
    boxSizing: "border-box",
  },
  filterColumn: {
    overflowY: "auto",
    flex:" 1 1 17%",
    minWidth: "200px",
    maxWidth: "250px",
    marginRight: "10px",
    backgroundColor: theme.palette.background.default,
    [theme.breakpoints.down('md')]: {
      display: "none"
    }
  },
  listColumn: {
    flex: "1 1 30%",
    minWidth: "250px",
    maxWidth: "400px",
    display: "block",
    position: "relative",
    overflowY: "auto",
    marginRight: "10px",
    backgroundColor: theme.palette.background.default,
    paddingTop: 0,
    [theme.breakpoints.down('sm')]: {
      minWidth: "100%",
      maxWidth: "100%",
      width: "100%"
    }
  },
  detailColumn: {
    flex: "2 1 50%",
    backgroundColor: theme.palette.background.default,
    display: "block",
  },
  filterMobileButton: {
    bottom: "10px",
    position: "absolute",
    right: "10px",
    display: "none",
    [theme.breakpoints.down('md')]: {
      display: "block"
    }
  },
  filterMobile: {
    width: 300,
    padding: 10
  },
  welcomeMsg: {
    paddingTop: "22%",
    backgroundColor: "transparent",
    boxShadow: "none",
    border: "none",
    justifyContent: "space-evenly",
    alignItems: "center",
    height: "100%",
    textAlign: "center",
    borderRadius: 0,
  },
}));

// ORDENAR OS TICKETS
const orderTickets = (tckts) => {
  if (!tckts || !Array.isArray(tckts) || tckts.length == 0) return [];

  const statusOrder = { open: 0, pending: 1, closed: 2 };

  return tckts.slice().sort((a, b) => {
    const statusA = statusOrder[a.status] ?? 99;
    const statusB = statusOrder[b.status] ?? 99;

    if (statusA !== statusB) {
      return statusA - statusB;
    }

    const dateA = a.lastMessageDate ? new Date(a.lastMessageDate).getTime() : 0;
    const dateB = b.lastMessageDate ? new Date(b.lastMessageDate).getTime() : 0;

    return dateB - dateA; // Mais recente primeiro
  });
}

const reducer = (state, action) => {

  if (action.type === "LOAD_TICKETS") {
    const newTickets = action.payload;
    return orderTickets([...newTickets]);
  }

  if (action.type === "RESET_UNREAD") {
    const { ticketId, lastMessage } = action.payload || {};
    const ticketIndex = state.findIndex(t => t.id === ticketId);
    if (ticketIndex !== -1) {
      state[ticketIndex].unreadMessages = 0;
      if (lastMessage) state[ticketIndex].lastMessage = lastMessage;
    }
    return orderTickets([...state]);
  }

  if (action.type === "UPDATE_TICKET") {

    const ticket = action.payload;
    const ticketIndex = state.findIndex(t => t.id == ticket.id && t.uuid == ticket.uuid);

    if (ticketIndex !== -1) {
      state[ticketIndex] = ticket;
    } else {
      state.unshift(ticket);
    }

    return orderTickets([...state]);
  }

  if (action.type === "UPDATE_TICKET_UNREAD_MESSAGES") {
    const ticket = action.payload;
    if (ticket?.queueId) {
      const ticketIndex = state.findIndex(t => t.id == ticket.id && t.uuid == ticket.uuid);
      if (ticketIndex !== -1) {
        state[ticketIndex] = ticket;
        state.unshift(...state.splice(ticketIndex, 1));
      } else {
        state.unshift(ticket);
      }
    }
    return orderTickets([...state]);
  }

  if (action.type === "UPDATE_TICKET_CONTACT") {
    const contact = action.payload;
    if (contact) {
      const ticketIndex = state.findIndex(t => t.contactId === contact?.id);
      if (ticketIndex !== -1) {
        state[ticketIndex].Contact = contact;
      }
    }
    return orderTickets([...state]);
  }

  if (action.type === "DELETE_TICKET") {
    const ticketId = action.payload;
    const ticketIndex = state.findIndex(t => t.id === ticketId);
    if (ticketIndex !== -1) state.splice(ticketIndex, 1);
    return orderTickets([...state]);
  }

  if (action.type === "RESET") {
    return [];
  }

  // Retorne o estado original se nenhuma ação corresponder
  return state;
};

const TicketManagement = () => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const { ticketId, contactId } = useParams();
  const { user } = useContext(AuthContext);

  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down('sm'));

  const [loading, setLoading] = useState(false);
  const [tickets, dispatch] = useReducer(reducer, []);
  const [totalCount, setTotalCount] = useState(0);
  const [contacts, setContacts] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [selectedTickets, setSelectedTickets] = useState([]);
  const [pressed, setPressed] = useState(false);
  const [order, setOrder] = useState('DESC');

  const [selectedsConnectionsAndQueues, setSelectedsConnectionsAndQueues] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [tags, setTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedUserStatus, setSelectedUserStatus] = useState([]);
  const userStatus = [
    {key: 'loggedIn', value: i18n.t("ticketManagement.filters.loggedIn")},
    {key: 'loggedOut', value: i18n.t("ticketManagement.filters.loggedOut")},
  ];
  const [selectedAssigned, setSelectedAssigned] = useState([]);
  const assigneds = [
    {key: 'assigned', value: i18n.t("ticketManagement.filters.assigned")},
    {key: 'unassigned', value: i18n.t("ticketManagement.filters.unassigned")},
  ];
  const [selectedTicketStatus, setSelectedTicketStatus] = useState([]);
  const ticketStatus = [
    {key: 'open', value: i18n.t("ticketManagement.filters.open")},
    {key: 'pending', value: i18n.t("ticketManagement.filters.pending")},
    {key: 'closed', value: i18n.t("ticketManagement.filters.closed")},
  ];

  const handleSocketEvents = (data) => {

		let ticket = data.ticket;
		if (!ticket && data?.Ticket) ticket = data.Ticket;
		if (!ticket && data?.message?.Ticket) ticket = data.message.Ticket;

		switch (data.action) {
			case "updateUnread":
        dispatch({ type: "RESET_UNREAD", payload: { ticketId: data.ticketId, lastMessage: data.ticket.lastMessage } });
				break;
			case "update":
        // verificar filtro, se passar atualizar, se não deletar
				if (shouldUpdateTicket(ticket) == true) dispatch({ type: "UPDATE_TICKET", payload: ticket });
				else dispatch({ type: "DELETE_TICKET", payload: ticket.id });
				break;
			case "delete":
        dispatch({ type: "DELETE_TICKET", payload: data.ticketId });
				break;
			case "create":
				if (shouldUpdateTicket(ticket)) dispatch({ type: "UPDATE_TICKET_UNREAD_MESSAGES", payload: ticket });
				break;
			case "updateContact":
        dispatch({ type: "UPDATE_TICKET_CONTACT", payload: data.contact });
				break;

			default:
				break;
		}
	};

  useEffect(() => {
    let isMounted = true;
    const abortController = new AbortController();

    (async () => {
      try {
        setLoading(true);
        const { data } = await api.get("/whatsappListAll", {
          params: { disabled: "0", userId: user?.id },
          signal: abortController.signal,
        });
        if (!isMounted || !data) return;

        // filtrar conexões que não tenham filas
        if (Array.isArray(data) && data.length > 0) {
          const filteredConnections = data.filter(dt => !isNaN(+dt?.id) && (dt?.Queues && Array.isArray(dt.Queues) && dt.Queues.length > 0));
          setSelectedsConnectionsAndQueues(filteredConnections.map(fc => { return { connectionId: fc.id, queueIds: fc.Queues.map(q => q.id) } }));
        }

        const { data: tagsData } = await api.get("/tags", {
          signal: abortController.signal,
        });
        if (!isMounted || !tagsData?.tags) return;
        setTags(tagsData.tags);

        const { data: usrData } = await api.get("/users-dropDown", {
          signal: abortController.signal,
        });
        if (!isMounted || !usrData) return;

        // remover usuário atual
        setUsers(usrData);
      } catch (err) {
        if (!isMounted || err.name == 'AbortError') return;
        console.error({ err });
        toastError(err);
      } finally {
        setTimeout(() => {
          setLoading(false);
        }, 2000);
      }
    })();

    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    const abortController = new AbortController();

    (async () => {
      try {
        setLoading(true);

        const { data } = await api.get("/ticketManagement", {
          params: {
            connectionsAndQueues: selectedsConnectionsAndQueues,
            userIds: selectedUsers,
            userStatus: selectedUserStatus,
            assigneds: selectedAssigned,
            ticketStatus: selectedTicketStatus,
            searchParam,
            pageNumber,
            pressed,
            order,
            tagIds: selectedTags
          },
          signal: abortController.signal,
        });
        if (!isMounted || !data) return;

        dispatch({ type: "LOAD_TICKETS", payload: data.tickets });

        setTotalCount(data.count);
        setHasMore(data.hasMore);
        setContacts(data.contacts);

      } catch (err) {
        if (!isMounted || err.name == 'AbortError') return;
        console.error({ err });
        toastError(err);
      } finally {
        setLoading(false);
      }
    })();

    const handleTicketUpdate = data => handleSocketEvents(data);
    const handleAppMessage = data => handleSocketEvents(data);
    const handleContactUpdate = data => handleSocketEvents({ ...data, action: "updateContact" });

    socket.on(`ticket-${user?.adminId}`, handleTicketUpdate);
    socket.on(`appMessage-${user?.adminId}`, handleAppMessage);
    socket.on(`contact-${user?.adminId}`, handleContactUpdate);

    return () => {
      isMounted = false;
      abortController.abort();

      socket.off(`ticket-${user?.adminId}`, handleTicketUpdate);
      socket.off(`appMessage-${user?.adminId}`, handleAppMessage);
      socket.off(`contact-${user?.adminId}`, handleContactUpdate);
    };
  }, [ , selectedsConnectionsAndQueues, selectedUsers, selectedUserStatus, selectedAssigned, selectedTicketStatus, pressed, order, selectedTags, pageNumber, searchParam]);

  const shouldUpdateTicket = (ticket) => {

    if (!ticket) return false;

    // Verificar consulta por texto
    if (searchParam && searchParam.trim() != "") return false;

    // Verificar etiquetas
    if (selectedTags && Array.isArray(selectedTags) && selectedTags.length > 0) {
      if (!Array.isArray(ticket?.Contact?.tagsId)) return false;
      if (ticket.Contact.tagIds.some(tagId => selectedTags.includes(tagId)) == false) return false;
    }

    // Verificar se há connectionAndQueuesIds válidos
    if (selectedsConnectionsAndQueues && Array.isArray(selectedsConnectionsAndQueues) && selectedsConnectionsAndQueues.length > 0) {
      // identificar conexão e fila
      const connIdx = selectedsConnectionsAndQueues.findIndex(scc => scc.connectionId == ticket.whatsappId);
      if (connIdx == -1) return false;
      if (selectedsConnectionsAndQueues[connIdx].queueIds.includes(ticket.queueId) == false) return false;
    }

    // Verificar atribuições de usuários
    if (selectedUsers && Array.isArray(selectedUsers) && selectedUsers.length > 0) {
      if (selectedUsers.includes(ticket.userId) == false) return false;
    }

    // Verificar se é para consultar os usuários onlines ou offlines no sistema
    // Não foi feito pq tem que consultar a todo momento a base de usuários online ou não

    // Verificar se é para consultar os atendimentos atribuídos ou não
    if (selectedAssigned && Array.isArray(selectedAssigned) && selectedAssigned.length > 0) {
      let isNull = false;
      let notNull = false;

      for (const ass of assigneds) {
        if (ass == 'assigned') notNull = true;
        else isNull = true;
      }

      if (isNull == true && notNull == false) {
        // sem atendente atribuído
        if (ticket?.userId && !isNaN(ticket.userId)) return false;
      } else if(isNull == false && notNull == true) {
        // com atendente atribuído
        if (!ticket?.userId && isNaN(ticket.userId)) return false;
      }
    }

    // Verificar status dos atendimentos
    if (selectedTicketStatus && Array.isArray(selectedTicketStatus) && selectedTicketStatus.length > 0) {
      if (selectedTicketStatus.includes(ticket.status) == false) return false;
    }

    // Verificar mensagens lidas
    if (pressed == true) {
      if (!ticket?.unreadMessages || ticket.unreadMessages == 0) return false;
    }

    return true;
  }

  const onSelectConnectionsAndQueues = (opts) => {
    setSelectedsConnectionsAndQueues(Array.isArray(opts) && opts.length > 0 ? opts : []);
    if(pageNumber > 1) setPageNumber(1);
  }

  const onSelectedUsers = (ids) => {
    setSelectedUsers(ids);
    if(pageNumber > 1) setPageNumber(1);
  }

  const onSelectedTags = (ids) => {
    setSelectedTags(ids);
    if(pageNumber > 1) setPageNumber(1);
  }

  const onSelectedUserStatus = (ids) => {
    setSelectedUserStatus(ids);
    if(pageNumber > 1) setPageNumber(1);
  }

  const onSelectAssigned = (ids) => {
    setSelectedAssigned(ids);
    if(pageNumber > 1) setPageNumber(1);
  }

  const onSelectTicketStatus = (ids) => {
    setSelectedTicketStatus(ids);
    if(pageNumber > 1) setPageNumber(1);
  }

  const handleDrawer = () => {
    setOpen(!open);
  };

  const handleSelectAll = () => {
		if (selectedTickets.length === tickets.length) {
			unSelectAll();
			return;
		}
		const allTicketIds = tickets.map(ticket => ticket.id);
		setSelectedTickets(allTicketIds);

	};

  const handleSelect = (ticketId) => {
    if (!ticketId && ticketId != '' && ticketId != 'undefined') return;

    setSelectedTickets(prev => prev.includes(ticketId) ? prev.filter(id => id !== ticketId) : [...prev, ticketId]);
  }

  const unSelectAll = () => {
    setSelectedTickets([]);
  }

  const handlePressed = () => {
    setPressed(prev => !prev);
    if(pageNumber > 1) setPageNumber(1);
  }

  const handleOrder = () => {
    setOrder(prev => prev == 'DESC' ? 'ASC' : 'DESC');
  }

  const RenderFilters = () => {
    return (
      <TicketManagementFilters
        selectedsConnectionsAndQueues={selectedsConnectionsAndQueues}
        onSelectConnectionsAndQueues={onSelectConnectionsAndQueues}
        users={users}
        onSelectedUsers={onSelectedUsers}
        selectedUsers={selectedUsers}
        userStatus={userStatus}
        onSelectedUserStatus={onSelectedUserStatus}
        selectedUserStatus={selectedUserStatus}
        assigneds={assigneds}
        selectedAssigned={selectedAssigned}
        onSelectAssigned={onSelectAssigned}
        ticketStatus={ticketStatus}
        selectedTicketStatus={selectedTicketStatus}
        onSelectTicketStatus={onSelectTicketStatus}
        tags={tags}
        selectedTags={selectedTags}
        onSelectedTags={onSelectedTags}
        searchParam={searchParam}
        handleSearchParam={handleSearchParam}
        user={user}
      />
    );
  }

  const handleScroll = e => {

		if (!hasMore || loading || searchParam != "") return;

		const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

		if (scrollHeight - (scrollTop + 100) < clientHeight) {
      setPageNumber(prev => prev + 1);
		}
	};

  const handleSearchParam = (txt) => {
    setSearchParam(txt);
    if(pageNumber > 1) setPageNumber(1);
  }

  return (
    <Box className={classes.mainContainer}>
      <Box className={classes.content}>
        {/* Filtros para monitores */}
        <Box
          component="aside"
          className={classNames({
            [classes.baseContent]: true,
            [classes.filterColumn]: true,
          })}
        >
          {RenderFilters()}
        </Box>

        {/* Filtros para mobiles */}
        <Drawer
          anchor="left"
          open={open}
          onClose={() => handleDrawer()}
          classes={{
            paper: classes.filterMobile
          }}
        >
          {RenderFilters()}
        </Drawer>

        {/* Lista de Atendimentos */}
        <Box
          component="section"
          className={classNames({
            [classes.baseContent]: true,
            [classes.listColumn]: true,
          })}
          onScroll={(e) => handleScroll(e)}
          hidden={isSm && (ticketId && ticketId != '' && ticketId != 'undefined')}
        >
          <Paper
            elevation={0}
            square
            style={{
              position: "relative",
              display: ticketId && ticketId != '' && ticketId != 'undefined' ? "flex" : "none",
              paddingTop: "10px",
              paddingBottom: "10px",
              color: "rgb(67, 83, 105)",
              background: "#fafafa",
              position: "sticky",
              top: "0",
              zIndex: "6"
            }}
          >
            <Avatar>
              <img
                id="picConnection"
                src={"/media/avatars/blank.png"}
                style={{ width: "40px" }}
              />
            </Avatar>
            <Box style={{ display: "flex", maxWidth: "calc(100% - 70px)" }}>
              <Typography
                component="p"
                id="definirFila"
                style={{ marginLeft: "15px", marginBottom: "-5px" }}
              >
              </Typography>
            </Box>
          </Paper>
          <TicketManagementList
            contacts={contacts} 
            tickets={tickets} 
            selectedTickets={selectedTickets} 
            handleSelectAll={handleSelectAll}
            handleSelect={handleSelect}
            unSelectAll={unSelectAll}
            loading={loading}
            pressed={pressed}
            order={order}
            handlePressed={handlePressed}
            handleOrder={handleOrder}
            totalCount={totalCount}
          />
        </Box>

        {/* Detalhes do Atendimento */}
        <Box
          component="main" 
          className={classNames({
            [classes.baseContent]: true,
            [classes.detailColumn]: true,
          })}
          hidden={isSm && (!ticketId || ticketId == '' || ticketId == 'undefined')}
        >
          {
            ticketId 
              ?
              <Ticket
                ticketId={ticketId}
                byTicketManagement={true}
              />
              :
              <Paper className={classes.welcomeMsg}>
                <img
                  src={Logo}
                  style={{
                    height: "180px",
                    width: "auto",
                    margin: "0 auto 10px auto",
                    display: "block"
                  }}
                />
                <b>{i18n.t("chat.noTicketMessage")}</b>
              </Paper>
          }
        </Box>

        {/* Botão de filtros para celular */}
        <Tooltip
          title={<Typography gutterBottom color="inherit">{i18n.t('ticketManagement.filters.title')}</Typography>}
        >
          <Fab
            color="primary"
            aria-label="add"
            size="small"
            className={classes.filterMobileButton}
            onClick={() => handleDrawer()}
          >
            <FilterListOutlinedIcon />
          </Fab>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default TicketManagement;